import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, faCalendarAlt, faClock, faTv, 
  faGlobe, faInfoCircle, faPlay, faPlus, 
  faStar, faShareAlt, faBookmark, faCheck, faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import { faStar as farStar } from '@fortawesome/free-regular-svg-icons';
import TrailerModal from './TrailerModal';
import MoviesModal from './MovieModal';
import { Link } from 'react-router-dom';

// ============================================================
//  GIFT-CODE AD-FREE CHECK (same as Navbar & MoviesModal)
// ============================================================
const ACTIVE_GIFT_CODES = ['SHREK', 'GARFIELD', 'TOMANDJERRY'];
const REVOKED_CODES = [''];

const isUserAdFree = () => {
  const redeemedCode = localStorage.getItem('adFreeCode');
  if (!redeemedCode) return false;
  return !REVOKED_CODES.includes(redeemedCode);
};

// Language code to full name mapping (for display)
const languageMap = {
  en: 'English',
  ru: 'Russian',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
  it: 'Italian',
  ja: 'Japanese',
  ko: 'Korean',
  zh: 'Chinese',
  hi: 'Hindi',
  pt: 'Portuguese',
  ar: 'Arabic',
  tr: 'Turkish',
  th: 'Thai',
  nl: 'Dutch',
  sv: 'Swedish',
  pl: 'Polish',
  da: 'Danish',
  no: 'Norwegian',
  fi: 'Finnish',
  el: 'Greek',
  cs: 'Czech',
  hu: 'Hungarian',
  he: 'Hebrew',
  id: 'Indonesian',
  ms: 'Malay',
  vi: 'Vietnamese',
  tl: 'Filipino',
  fa: 'Persian',
  bn: 'Bengali',
  ta: 'Tamil',
  te: 'Telugu',
  mr: 'Marathi',
  kn: 'Kannada',
  ml: 'Malayalam',
  ur: 'Urdu',
  uk: 'Ukrainian',
  ro: 'Romanian',
  sk: 'Slovak',
  bg: 'Bulgarian',
  hr: 'Croatian',
  sr: 'Serbian',
  sl: 'Slovenian',
  et: 'Estonian',
  lv: 'Latvian',
  lt: 'Lithuanian',
  ka: 'Georgian',
  hy: 'Armenian',
  sq: 'Albanian',
  mk: 'Macedonian',
};

const getLanguageName = (code) => {
  if (!code) return 'N/A';
  return languageMap[code] || code.toUpperCase();
};

// Map our language codes to TMDB locale codes
const getTmdbLanguage = (lang) => {
  const map = {
    en: 'en-US',
    fr: 'fr-FR',
    es: 'es-ES',
    it: 'it-IT',
    nl: 'nl-NL',
  };
  return map[lang] || 'en-US';
};

// ================== MODAL TRANSLATIONS ==================
const modalTranslations = {
  en: {
    noOverview: "No overview available.",
    loadingSeries: "Loading series details...",
    seasonsEpisodes: "Seasons & Episodes",
    selectSeason: "Select Season:",
    seasonLabel: "Season",
    episodesOf: (current, total) => `Episodes (${current} of ${total})`,
    episodeNotReleased: "Episode not released yet",
    lastWatched: "Last Watched",
    noDescription: "No description available.",
    topCast: "Top Cast",
    supportingCast: "Supporting Cast",
    filmography: "Filmography",
    movies: "Movies",
    tvShows: "TV Shows",
    noFilmography: (type) => `No ${type} found with posters and at least 80 votes`,
    loadingFilmography: "Loading filmography...",
    votes: (count) => `(${count.toLocaleString()} votes)`,
    back: "Back",
    loadMoreEpisodes: (remaining) => `Load More Episodes (${remaining} remaining)`,
    seasonNotReleased: "Season not released yet",
  },
  fr: {
    noOverview: "Aucun aperçu disponible.",
    loadingSeries: "Chargement des détails de la série...",
    seasonsEpisodes: "Saisons et épisodes",
    selectSeason: "Sélectionner la saison :",
    seasonLabel: "Saison",
    episodesOf: (current, total) => `Épisodes (${current} sur ${total})`,
    episodeNotReleased: "Épisode non encore diffusé",
    lastWatched: "Dernier visionnage",
    noDescription: "Aucune description disponible.",
    topCast: "Distribution principale",
    supportingCast: "Distribution secondaire",
    filmography: "Filmographie",
    movies: "Films",
    tvShows: "Séries TV",
    noFilmography: (type) => `Aucun ${type} trouvé avec des affiches et au moins 80 votes`,
    loadingFilmography: "Chargement de la filmographie...",
    votes: (count) => `(${count.toLocaleString()} votes)`,
    back: "Retour",
    loadMoreEpisodes: (remaining) => `Charger plus d'épisodes (${remaining} restants)`,
    seasonNotReleased: "Saison non encore diffusée",
  },
  es: {
    noOverview: "No hay resumen disponible.",
    loadingSeries: "Cargando detalles de la serie...",
    seasonsEpisodes: "Temporadas y episodios",
    selectSeason: "Seleccionar temporada:",
    seasonLabel: "Temporada",
    episodesOf: (current, total) => `Episodios (${current} de ${total})`,
    episodeNotReleased: "Episodio no emitido aún",
    lastWatched: "Último visto",
    noDescription: "Sin descripción disponible.",
    topCast: "Reparto principal",
    supportingCast: "Reparto secundario",
    filmography: "Filmografía",
    movies: "Películas",
    tvShows: "Series TV",
    noFilmography: (type) => `No se encontraron ${type} con pósteres y al menos 80 votos`,
    loadingFilmography: "Cargando filmografía...",
    votes: (count) => `(${count.toLocaleString()} votos)`,
    back: "Volver",
    loadMoreEpisodes: (remaining) => `Cargar más episodios (${remaining} restantes)`,
    seasonNotReleased: "Temporada no emitida aún",
  },
  it: {
    noOverview: "Nessuna panoramica disponibile.",
    loadingSeries: "Caricamento dettagli della serie...",
    seasonsEpisodes: "Stagioni ed episodi",
    selectSeason: "Seleziona stagione:",
    seasonLabel: "Stagione",
    episodesOf: (current, total) => `Episodi (${current} di ${total})`,
    episodeNotReleased: "Episodio non ancora trasmesso",
    lastWatched: "Ultimo visto",
    noDescription: "Nessuna descrizione disponibile.",
    topCast: "Cast principale",
    supportingCast: "Cast di supporto",
    filmography: "Filmografia",
    movies: "Film",
    tvShows: "Serie TV",
    noFilmography: (type) => `Nessun ${type} trovato con poster e almeno 80 voti`,
    loadingFilmography: "Caricamento filmografia...",
    votes: (count) => `(${count.toLocaleString()} voti)`,
    back: "Indietro",
    loadMoreEpisodes: (remaining) => `Carica altri episodi (${remaining} rimanenti)`,
    seasonNotReleased: "Stagione non ancora trasmessa",
  },
  nl: {
    noOverview: "Geen overzicht beschikbaar.",
    loadingSeries: "Seriegegevens laden...",
    seasonsEpisodes: "Seizoenen en afleveringen",
    selectSeason: "Selecteer seizoen:",
    seasonLabel: "Seizoen",
    episodesOf: (current, total) => `Afleveringen (${current} van ${total})`,
    episodeNotReleased: "Aflevering nog niet uitgezonden",
    lastWatched: "Laatst bekeken",
    noDescription: "Geen beschrijving beschikbaar.",
    topCast: "Hoofdcast",
    supportingCast: "Ondersteunende cast",
    filmography: "Filmografie",
    movies: "Films",
    tvShows: "TV-series",
    noFilmography: (type) => `Geen ${type} gevonden met posters en minimaal 80 stemmen`,
    loadingFilmography: "Filmografie laden...",
    votes: (count) => `(${count.toLocaleString()} stemmen)`,
    back: "Terug",
    loadMoreEpisodes: (remaining) => `Meer afleveringen laden (${remaining} resterend)`,
    seasonNotReleased: "Seizoen nog niet uitgezonden",
  },
};

// Star rating component for reuse
const StarRating = ({ rating, size = '1x' }) => {
  const starRating = rating / 2;
  const fullStars = Math.floor(starRating);
  const partialStar = starRating - fullStars;
  const emptyStars = 5 - Math.ceil(starRating);
  
  return (
    <div className="star-rating" style={{ display: 'flex', justifyContent: 'center' }}>
      {[...Array(fullStars)].map((_, i) => (
        <div key={`full-${i}`} className="star" style={{ fontSize: size }}>
          <FontAwesomeIcon icon={farStar} />
          <div className="star-filled" style={{ width: '100%' }}>
            <FontAwesomeIcon icon={faStar} />
          </div>
        </div>
      ))}
      {partialStar > 0 && (
        <div key="partial" className="star" style={{ fontSize: size }}>
          <FontAwesomeIcon icon={farStar} />
          <div className="star-filled" style={{ width: `${partialStar * 100}%` }}>
            <FontAwesomeIcon icon={faStar} />
          </div>
        </div>
      )}
      {[...Array(emptyStars)].map((_, i) => (
        <div key={`empty-${i}`} className="star" style={{ fontSize: size }}>
          <FontAwesomeIcon icon={farStar} />
          <div className="star-filled" style={{ width: '0%' }}>
            <FontAwesomeIcon icon={faStar} />
          </div>
        </div>
      ))}
    </div>
  );
};

StarRating.propTypes = {
  rating: PropTypes.number.isRequired,
  size: PropTypes.string
};

const SeriesModal = ({ isOpen, onClose, seriesId, onUpdate, onOpenMovie, history, onBack }) => {
  const [seriesDetails, setSeriesDetails] = useState(null);
  const [credits, setCredits] = useState(null);
  const [videos, setVideos] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(1);
  const [seasonData, setSeasonData] = useState(null);
  const [showTrailerModal, setShowTrailerModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [episodePage, setEpisodePage] = useState(1);
  const [isAdded, setIsAdded] = useState(false);
  const [selectedEpisodeId, setSelectedEpisodeId] = useState(null);
  const [showEpisodeActions, setShowEpisodeActions] = useState(false);
  const [actorBackdrop, setActorBackdrop] = useState(null);
  const [lastWatchedEpisode, setLastWatchedEpisode] = useState(null);
  const episodesPerPage = 25;
  const modalRef = useRef(null);
  
  // Actor modal state
  const [selectedActor, setSelectedActor] = useState(null);
  const [actorFilmography, setActorFilmography] = useState(null);
  const [contentFilter, setContentFilter] = useState('movies');
  const [isMobile, setIsMobile] = useState(false);
  
  // Language state
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('preferredLanguage') || 'en';
  });

  const apiKey = '57240db50c2008e78c261e1a934627e4';
  const actorModalRef = useRef(null);
  const trailerModalRef = useRef(null);
  const actorFallbackImage = 'https://i.ibb.co/Lkf9x3W/hsaSDasa.png';

  // ========== AD LINK (shared for all play buttons) ==========
  const adLink = 'https://amrox.org/4/a15e5a4291b030aa61dbb4b98c952698';

  // Navigation history state - simplified
  const [navigationStack, setNavigationStack] = useState([]);
  
  // Movie modal state
  const [selectedMovie, setSelectedMovie] = useState(null);

  // Translation helper
  const t = (key, ...args) => {
    const langData = modalTranslations[language] || modalTranslations.en;
    const keys = key.split('.');
    let value = langData;
    for (const k of keys) {
      if (value && value[k] !== undefined) {
        value = value[k];
      } else {
        // fallback to English
        const enData = modalTranslations.en;
        let fallback = enData;
        for (const fk of keys) {
          if (fallback && fallback[fk] !== undefined) {
            fallback = fallback[fk];
          } else {
            return key; // fallback to key itself
          }
        }
        if (typeof fallback === 'function') {
          return fallback(...args);
        }
        return fallback;
      }
    }
    if (typeof value === 'function') {
      return value(...args);
    }
    return value;
  };

  // Helper functions for API calls
  const fetchContentDetails = async (type, id, lang) => {
    const tmdbLang = getTmdbLanguage(lang || language);
    const response = await fetch(
      `https://api.themoviedb.org/3/${type}/${id}?api_key=${apiKey}&language=${tmdbLang}`
    );
    return await response.json();
  };

  const fetchCredits = async (type, id, lang) => {
    const tmdbLang = getTmdbLanguage(lang || language);
    const response = await fetch(
      `https://api.themoviedb.org/3/${type}/${id}/credits?api_key=${apiKey}&language=${tmdbLang}`
    );
    return await response.json();
  };

  // ========== FIX: Always use English for videos (trailers) ==========
  const fetchVideos = async (type, id) => {
    // Force 'en-US' to get English trailer regardless of user language
    const response = await fetch(
      `https://api.themoviedb.org/3/${type}/${id}/videos?api_key=${apiKey}&language=en-US`
    );
    return await response.json();
  };
  // =================================================================

  const fetchSeasonDetails = async (seriesId, seasonNumber, lang) => {
    const tmdbLang = getTmdbLanguage(lang || language);
    const response = await fetch(
      `https://api.themoviedb.org/3/tv/${seriesId}/season/${seasonNumber}?api_key=${apiKey}&language=${tmdbLang}`
    );
    return await response.json();
  };

  const loadSeriesData = async (id, lang) => {
    setIsLoading(true);
    try {
      // Fetch series details twice: one for English title, one for everything else in user language
      const [detailsEn, detailsUser] = await Promise.all([
        fetchContentDetails('tv', id, 'en-US'), // for title
        fetchContentDetails('tv', id, lang)      // for overview, genres, etc.
      ]);
      
      // Merge: use the English title, but keep all other fields from the user-language version
      const mergedDetails = {
        ...detailsUser,
        name: detailsEn.name, // force English title
        original_name: detailsEn.original_name || detailsEn.name,
      };
      
      setSeriesDetails(mergedDetails);
      
      // Fetch credits (user language) and videos (ALWAYS English)
      const [creditsData, videosData] = await Promise.all([
        fetchCredits('tv', id, lang),
        fetchVideos('tv', id)   // <-- videos always in English
      ]);
      
      setCredits(creditsData);
      setVideos(videosData);
      
      const watchlist = JSON.parse(localStorage.getItem('watchlist')) || [];
      const isSeriesAlreadyAdded = watchlist.some(item => 
        item.id === mergedDetails.id && item.type === 'tv'
      );
      setIsAdded(isSeriesAlreadyAdded);
      
      const lastWatchedKey = `lastWatched_${id}`;
      const storedLastWatched = localStorage.getItem(lastWatchedKey);
      let initialSeason = 1;
      let storedEpisodeId = null;

      if (storedLastWatched) {
        const lastWatched = JSON.parse(storedLastWatched);
        if (lastWatched.season >= 1 && lastWatched.season <= mergedDetails.number_of_seasons) {
          initialSeason = lastWatched.season;
          storedEpisodeId = lastWatched.episodeId;
        }
      }

      setSelectedSeason(initialSeason);
      
      if (mergedDetails.number_of_seasons > 0) {
        const season = await fetchSeasonDetails(id, initialSeason, lang);
        setSeasonData(season);
        
        if (storedEpisodeId) {
          const episodeExists = season.episodes.some(ep => ep.id === storedEpisodeId);
          if (episodeExists) {
            setLastWatchedEpisode(storedEpisodeId);
          }
        }
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching series details:', error);
      setIsLoading(false);
    }
  };

  // Listen to language changes and reload data if modal is open
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'preferredLanguage') {
        const newLang = e.newValue || 'en';
        setLanguage(newLang);
        if (isOpen && seriesId) {
          loadSeriesData(seriesId, newLang);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [isOpen, seriesId]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setSeriesDetails(null);
      setCredits(null);
      setVideos(null);
      setSeasonData(null);
      setSelectedSeason(1);
      setEpisodePage(1);
      setIsAdded(false);
      setSelectedEpisodeId(null);
      setShowEpisodeActions(false);
      setSelectedActor(null);
      setNavigationStack([]);
      setActorBackdrop(null);
      setLastWatchedEpisode(null);
      setSelectedMovie(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !seriesId) return;
    loadSeriesData(seriesId, language);
  }, [isOpen, seriesId]);

  useEffect(() => {
    if (isOpen && seriesDetails) {
      // Dispatch custom event to notify Navbar
      const seriesOpenedEvent = new CustomEvent('seriesOpened', { 
        detail: {
          id: seriesDetails.id,
          name: seriesDetails.name,
          poster_path: seriesDetails.poster_path,
          media_type: 'tv',
          vote_average: seriesDetails.vote_average,
          first_air_date: seriesDetails.first_air_date
        }
      });
      window.dispatchEvent(seriesOpenedEvent);
    }
  }, [isOpen, seriesDetails]);

  useEffect(() => {
    if (!selectedActor) return;
    
    const fetchFilmography = async () => {
      try {
        const tmdbLang = getTmdbLanguage(language);
        const response = await fetch(
          `https://api.themoviedb.org/3/person/${selectedActor.id}/combined_credits?api_key=${apiKey}&language=${tmdbLang}`
        );
        const data = await response.json();
        setActorFilmography(data);
      } catch (error) {
        console.error('Error fetching actor filmography:', error);
      }
    };

    fetchFilmography();
  }, [selectedActor, apiKey, language]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        if (showTrailerModal) {
          setShowTrailerModal(false);
        } 
        else if (selectedMovie) {
          handleCloseMovieModal();
        }
        else if (selectedActor) {
          handleCloseActorModal();
        } else {
          onClose();
        }
      }
    };
    
    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
    }
    
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose, selectedActor, showTrailerModal, selectedMovie]);

  const handleAddToWatchlist = () => {
    if (!seriesDetails) return;
    
    const watchlist = JSON.parse(localStorage.getItem('watchlist')) || [];
    const isSeriesAlreadyAdded = watchlist.some(item => 
      item.id === seriesDetails.id && item.type === 'tv'
    );
    
    let updatedWatchlist;
    
    if (isSeriesAlreadyAdded) {
      updatedWatchlist = watchlist.filter(item => 
        !(item.id === seriesDetails.id && item.type === 'tv')
      );
    } else {
      const seriesData = {
        id: seriesDetails.id,
        title: seriesDetails.name,
        poster_path: seriesDetails.poster_path,
        first_air_date: seriesDetails.first_air_date,
        vote_average: seriesDetails.vote_average,
        type: 'tv'
      };
      updatedWatchlist = [...watchlist, seriesData];
    }
    
    localStorage.setItem('watchlist', JSON.stringify(updatedWatchlist));
    setIsAdded(!isSeriesAlreadyAdded);
    if (onUpdate) onUpdate();
  };

  const handleSeasonChange = async (seasonNumber) => {
    setSelectedSeason(seasonNumber);
    setEpisodePage(1);
    setSelectedEpisodeId(null);
    setShowEpisodeActions(false);
    
    try {
      const season = await fetchSeasonDetails(seriesId, seasonNumber, language);
      setSeasonData(season);
      
      const lastWatchedKey = `lastWatched_${seriesId}`;
      const storedLastWatched = localStorage.getItem(lastWatchedKey);
      
      if (storedLastWatched) {
        const lastWatched = JSON.parse(storedLastWatched);
        if (lastWatched.season === seasonNumber) {
          const episodeExists = season.episodes.some(ep => ep.id === lastWatched.episodeId);
          if (episodeExists) {
            setLastWatchedEpisode(lastWatched.episodeId);
            return;
          }
        }
      }
    } catch (error) {
      console.error('Error fetching season details:', error);
    }
  };

  const handleEpisodeClick = (episodeId) => {
    if (selectedEpisodeId === episodeId) {
      setSelectedEpisodeId(null);
      setShowEpisodeActions(false);
    } else {
      setSelectedEpisodeId(episodeId);
      setShowEpisodeActions(true);
    }
  };

  const handleActionClick = (episodeId) => {
    const lastWatchedKey = `lastWatched_${seriesId}`;
    const lastWatchedData = {
      season: selectedSeason,
      episodeId: episodeId
    };
    localStorage.setItem(lastWatchedKey, JSON.stringify(lastWatchedData));
    setLastWatchedEpisode(episodeId);
  };

  // ========== HANDLE PLAY BUTTON CLICK (ad-free aware) ==========
  const handlePlayClick = () => {
    // Open the ad link only if the user is NOT ad-free
    if (!isUserAdFree()) {
      window.open(adLink, '_blank');
    }
    // Otherwise, do nothing – the <Link> will navigate normally
  };

  const handleBackClick = () => {
    if (navigationStack.length > 0) {
      const lastAction = navigationStack[navigationStack.length - 1];
      
      if (lastAction.type === 'actor') {
        setSelectedActor(lastAction.data);
        setNavigationStack(prev => prev.slice(0, -1));
      } 
      else if (lastAction.type === 'movie') {
        setSelectedMovie(lastAction.data);
        setNavigationStack(prev => prev.slice(0, -1));
      }
    } else if (onBack) {
      onBack();
    } else {
      onClose();
    }
  };

  const handleActorClick = (person) => {
    setNavigationStack(prev => [...prev, { 
      type: 'series',
      data: { seriesDetails, credits, videos, seasonData, selectedSeason }
    }]);
    
    setSelectedActor(person);
    setActorBackdrop(
      seriesDetails?.backdrop_path 
        ? `https://image.tmdb.org/t/p/w780${seriesDetails.backdrop_path}`
        : 'https://via.placeholder.com/1920x1080?text=No+Backdrop'
    );
  };

  const handleCloseActorModal = () => {
    if (navigationStack.length > 0 && navigationStack[navigationStack.length - 1].type === 'series') {
      const seriesData = navigationStack[navigationStack.length - 1].data;
      setSeriesDetails(seriesData.seriesDetails);
      setCredits(seriesData.credits);
      setVideos(seriesData.videos);
      setSeasonData(seriesData.seasonData);
      setSelectedSeason(seriesData.selectedSeason);
      setNavigationStack(prev => prev.slice(0, -1));
    }
    setSelectedActor(null);
    setActorFilmography(null);
    setContentFilter('movies');
  };

  const handleOpenTVShow = (newSeriesId) => {
    setNavigationStack(prev => [...prev, { 
      type: 'actor',
      data: selectedActor
    }]);
    
    setSeriesDetails(null);
    setCredits(null);
    setVideos(null);
    setSeasonData(null);
    setSelectedSeason(1);
    setEpisodePage(1);
    setIsAdded(false);
    setSelectedEpisodeId(null);
    setShowEpisodeActions(false);
    setSelectedActor(null);
    setLastWatchedEpisode(null);
    
    loadSeriesData(newSeriesId, language);
  };

  const handleMovieSelect = (movie) => {
    setNavigationStack(prev => [...prev, { 
      type: 'actor',
      data: selectedActor
    }]);
    
    setSelectedMovie(movie);
    setSelectedActor(null);
  };

  const handleCloseMovieModal = () => {
    if (navigationStack.length > 0 && navigationStack[navigationStack.length - 1].type === 'actor') {
      const actorData = navigationStack[navigationStack.length - 1].data;
      setSelectedActor(actorData);
      setNavigationStack(prev => prev.slice(0, -1));
    }
    setSelectedMovie(null);
  };

  const filteredFilmography = () => {
    if (!actorFilmography) return [];
    
    const uniqueItemsMap = new Map();
    
    actorFilmography.cast.forEach(item => {
      if (
        (contentFilter === 'movies' && 
         item.media_type === 'movie' && 
         item.poster_path &&
         (item.vote_count || 0) >= 80) ||
        (contentFilter === 'tv' && 
         item.media_type === 'tv' && 
         item.poster_path &&
         (item.vote_count || 0) >= 80 &&
         !item.genre_ids?.some(id => [10763, 10767, 10764].includes(id)))
      ) {
        if (!uniqueItemsMap.has(item.id)) {
          uniqueItemsMap.set(item.id, item);
        }
      }
    });
    
    const filtered = Array.from(uniqueItemsMap.values());
    
    filtered.sort((a, b) => {
      const dateA = a.release_date || a.first_air_date;
      const dateB = b.release_date || b.first_air_date;
      return dateB ? (dateA ? new Date(dateB) - new Date(dateA) : -1) : 1;
    });
    
    return filtered;
  };

  if (!isOpen) return null;

  const backdropPath = seriesDetails?.backdrop_path 
    ? `https://image.tmdb.org/t/p/w780${seriesDetails.backdrop_path}`
    : 'https://via.placeholder.com/1920x1080?text=No+Backdrop';

  const posterPath = seriesDetails?.poster_path 
    ? `https://image.tmdb.org/t/p/w780${seriesDetails.poster_path}`
    : 'https://via.placeholder.com/500x750?text=No+Poster';

  const trailer = videos?.results?.find(video => video.type === 'Trailer');

  const episodes = seasonData?.episodes || [];
  const totalEpisodes = episodes.length;
  const currentEpisodes = episodes.slice(0, episodePage * episodesPerPage);
  const hasMoreEpisodes = totalEpisodes > currentEpisodes.length;

  const topCastCount = isMobile ? 4 : 5;
  const supportingCastCount = isMobile ? 4 : 5;

  const actionButtons = (
    <div className="modal-actions-container">
      <div className="modal-actions">
        <button 
          className={`action-btn trailer-btn ${!trailer ? 'disabled' : ''}`}
          id="seriesTrailerBtn"
          onClick={() => trailer && setShowTrailerModal(true)}
          disabled={!trailer}
        >
          <FontAwesomeIcon icon={faPlay} />
          {trailer ? 'Watch Trailer' : 'Trailer Not Available'}
        </button>
        <button 
          className={`action-btn add-btn ${isAdded ? 'added' : ''}`} 
          onClick={handleAddToWatchlist}
        >
          <FontAwesomeIcon icon={isAdded ? faCheck : faPlus} />
          {isAdded ? "In Your List" : "Add to Watchlist"}
        </button>
      </div>
    </div>
  );

  const castSection = (
    <div className="modal-cast">
      <h3 className="cast-title">{t('topCast')}</h3>
      <div className="cast-grid" id="seriesCastGrid" style={{ 
        display: 'grid',
        gridTemplateColumns: `repeat(${isMobile ? 4 : 5}, 1fr)`,
        gap: '15px',
        marginBottom: '30px'
      }}>
        {credits?.cast?.slice(0, topCastCount).map(person => {
          const photoPath = person.profile_path 
            ? `https://image.tmdb.org/t/p/w200${person.profile_path}`
            : actorFallbackImage;
          
          return (
            <div 
              key={person.credit_id}
              className="cast-member"
              onClick={() => handleActorClick(person)}
              style={{ cursor: 'pointer', textAlign: 'center' }}
            >
              <div style={{
                position: 'relative',
                width: isMobile ? '70px' : '100px',
                height: isMobile ? '70px' : '100px',
                borderRadius: '50%',
                overflow: 'hidden',
                margin: '0 auto 8px',
                border: '2px solid rgba(255, 255, 255, 0.1)'
              }}>
                <img 
                  src={photoPath} 
                  alt={person.name} 
                  style={{ 
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = actorFallbackImage;
                  }}
                />
              </div>
              <div className="cast-name" style={{ 
                fontSize: isMobile ? '0.8rem' : '0.9rem',
                fontWeight: 'bold',
                marginBottom: '4px'
              }}>
                {person.name}
              </div>
              <div className="cast-character" style={{ 
                fontSize: isMobile ? '0.7rem' : '0.8rem',
                color: '#aaa'
              }}>
                {person.character}
              </div>
            </div>
          );
        })}
      </div>
      
      <h3 className="cast-title">{t('supportingCast')}</h3>
      <div className="cast-grid" id="supportingCastGrid" style={{ 
        display: 'grid',
        gridTemplateColumns: `repeat(${isMobile ? 4 : 5}, 1fr)`,
        gap: '15px',
        marginBottom: '30px'
      }}>
        {credits?.cast?.slice(topCastCount, topCastCount + supportingCastCount).map(person => {
          const photoPath = person.profile_path 
            ? `https://image.tmdb.org/t/p/w200${person.profile_path}`
            : actorFallbackImage;
          
          return (
            <div 
              key={person.credit_id}
              className="cast-member"
              onClick={() => handleActorClick(person)}
              style={{ cursor: 'pointer', textAlign: 'center' }}
            >
              <div style={{
                position: 'relative',
                width: isMobile ? '70px' : '100px',
                height: isMobile ? '70px' : '100px',
                borderRadius: '50%',
                overflow: 'hidden',
                margin: '0 auto 8px',
                border: '2px solid rgba(255, 255, 255, 0.1)'
              }}>
                <img 
                  src={photoPath} 
                  alt={person.name} 
                  style={{ 
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = actorFallbackImage;
                  }}
                />
              </div>
              <div className="cast-name" style={{ 
                fontSize: isMobile ? '0.8rem' : '0.9rem',
                fontWeight: 'bold',
                marginBottom: '4px'
              }}>
                {person.name}
              </div>
              <div className="cast-character" style={{ 
                fontSize: isMobile ? '0.7rem' : '0.8rem',
                color: '#aaa'
              }}>
                {person.character}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      <div className={`series-modal ${isOpen ? 'active' : ''}`} id="seriesModal">
        <div 
          className="modal-backdrop" 
          id="seriesModalBackdrop"
          style={{ backgroundImage: `url(${backdropPath})` }}
        ></div>
        <div className="modal-overlay"></div>
        <div className="modal-content" ref={modalRef}>
          <div className="modal-header">
            {(navigationStack.length > 0 || onBack) && (
              <div 
                className="back-modal" 
                onClick={handleBackClick}
                style={{
                  position: 'absolute',
                  top: '30px',
                  right: '90px',
                  background: 'rgba(255, 255, 255, 0.15)',
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  zIndex: 10,
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                <FontAwesomeIcon icon={faArrowLeft} style={{ color: 'white', fontSize: '24px' }} />
              </div>
            )}
            <div className="close-modal" onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </div>
          </div>
          <div className="modal-body">
            {isLoading ? (
              <div className="modal-loading">
                <div className="spinner"></div>
                <p>{t('loadingSeries')}</p>
              </div>
            ) : (
              <>
                <div className="modal-poster">
                  <img 
                    src={posterPath} 
                    alt={seriesDetails?.name} 
                    id="seriesModalPoster" 
                    loading="lazy"
                  />
                </div>
                <div className="modal-details">
                  <h1 className="modal-title" id="seriesModalTitle">
                    {seriesDetails?.name}
                  </h1>
                  <p className="modal-tagline" id="seriesModalTagline">
                    {seriesDetails?.tagline || seriesDetails?.type}
                  </p>
                  
                  <div className="modal-meta">
                    <div className="meta-item" id="seriesMetaYear">
                      <FontAwesomeIcon icon={faCalendarAlt} />
                      <span id="seriesModalYear">
                        {seriesDetails?.first_air_date 
                          ? new Date(seriesDetails.first_air_date).getFullYear() 
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="meta-item" id="seriesMetaSeasons">
                      <FontAwesomeIcon icon={faTv} />
                      <span id="seriesModalSeasons">
                        {seriesDetails?.number_of_seasons} season{seriesDetails?.number_of_seasons !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="meta-item">
                      <FontAwesomeIcon icon={faGlobe} />
                      <span id="seriesModalLanguage">
                        {getLanguageName(seriesDetails?.original_language)}
                      </span>
                    </div>
                    <div className="meta-item" id="seriesMetaStatus">
                      <FontAwesomeIcon icon={faInfoCircle} />
                      <span id="seriesModalStatus">
                        {seriesDetails?.status}
                      </span>
                    </div>
                  </div>
                  
                  <div className="modal-rating">
                    <div className="rating-value" id="seriesModalRating">
                      {seriesDetails?.vote_average?.toFixed(1)}
                    </div>
                    <div className="rating-count" id="seriesModalVoteCount">
                      {t('votes', seriesDetails?.vote_count || 0)}
                    </div>
                  </div>
                  
                  <div className="modal-genres" id="seriesModalGenres">
                    {seriesDetails?.genres?.map(genre => (
                      <div key={genre.id} className="genre">
                        {genre.name}
                      </div>
                    ))}
                  </div>
                  
                  <p className="modal-overview" id="seriesModalOverview">
                    {seriesDetails?.overview || t('noOverview')}
                  </p>
                  
                  {!isMobile && actionButtons}
                  
                  {isMobile && castSection}
                  
                  {seriesDetails?.number_of_seasons > 0 && (
                    <div className="seasons-container" id="seasonsContainer">
                      {isMobile && actionButtons}
                      
                      <h3 className="seasons-title">{t('seasonsEpisodes')}</h3>
                      <div className="season-selector">
                        <label className="season-label" htmlFor="seasonSelect">
                          {t('selectSeason')}
                        </label>
                        <select 
                          id="seasonSelect" 
                          className="season-select"
                          value={selectedSeason}
                          onChange={(e) => handleSeasonChange(parseInt(e.target.value))}
                        >
                          {[...Array(seriesDetails.number_of_seasons)].map((_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {t('seasonLabel')} {i + 1}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="episodes-container" id="episodesContainer">
                        {totalEpisodes > 0 ? (
                          <>
                            <h4 className="episode-list-title">
                              {t('episodesOf', currentEpisodes.length, totalEpisodes)}
                            </h4>
                            {currentEpisodes.map(episode => {
                              const stillPath = episode.still_path 
                                ? `https://image.tmdb.org/t/p/w500${episode.still_path}`
                                : 'https://via.placeholder.com/500x280?text=No+Image';
                              
                              const isUnreleased = !episode.still_path;
                              const isLastWatchedOnDesktop = lastWatchedEpisode === episode.id && !isMobile;
                              
                              return (
                                <div 
                                  key={episode.id} 
                                  className={`episode-item ${selectedEpisodeId === episode.id ? 'selected' : ''}`}
                                  onClick={isUnreleased ? undefined : () => handleEpisodeClick(episode.id)}
                                  style={{ overflow: 'hidden' }}
                                >
                                  <div className="episode-header">
                                    <div className="episode-number">
                                      {String(episode.episode_number).padStart(2, '0')}
                                      {lastWatchedEpisode === episode.id && (
                                        <span className="last-watched-tag">{t('lastWatched')}</span>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {isUnreleased ? (
                                    <div className="unreleased-episode" style={{
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      minHeight: isMobile ? '200px' : '150px',
                                      width: '100%',
                                      color: '#aaa',
                                      fontStyle: 'italic'
                                    }}>
                                      {t('episodeNotReleased')}
                                    </div>
                                  ) : (
                                    <div className="episode-body" style={{ 
                                      display: 'flex', 
                                      flexDirection: isMobile ? 'column' : 'row',
                                      overflow: 'hidden'
                                    }}>
                                      <div 
                                        className="episode-image-container"
                                        style={{
                                          position: 'relative',
                                          width: isMobile ? '100%' : '35%',
                                          aspectRatio: '16/9',
                                          overflow: 'hidden',
                                          flexShrink: 0,
                                          borderRadius: '8px'
                                        }}
                                      >
                                        <img 
                                          src={stillPath} 
                                          alt={episode.name}
                                          className="episode-image"
                                          loading="lazy"
                                          style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            display: 'block',
                                            transition: 'none',
                                            transform: 'none'
                                          }}
                                        />
                                      </div>
                                      <div 
                                        className="episode-content" 
                                        style={{ 
                                          width: isMobile ? '100%' : '65%',
                                          padding: isMobile ? '10px 0 0' : '0 0 0 15px'
                                        }}
                                      >
                                        <div className="episode-content-header">
                                          <h4 className="episode-title">{episode.name}</h4>
                                          <div className="episode-date">
                                            {episode.air_date 
                                              ? new Date(episode.air_date).toLocaleDateString() 
                                              : 'TBA'}
                                          </div>
                                        </div>
                                        
                                        <div className={`episode-details ${selectedEpisodeId === episode.id ? 'hidden' : ''}`}>
                                          <p className="episode-overview">
                                            {episode.overview 
                                              ? episode.overview.substring(0, 100) + (episode.overview.length > 100 ? '...' : '')
                                              : t('noDescription')}
                                          </p>
                                          <div className="episode-meta" style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            color: '#aaa',
                                            marginTop: '8px'
                                          }}>
                                            <div className="episode-runtime" style={{ 
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '4px'
                                            }}>
                                              <FontAwesomeIcon icon={faClock} />
                                              <span>{episode.runtime || '?'} min</span>
                                            </div>
                                            {episode.vote_average > 0 && (
                                              <div className="episode-rating" >
                                                <FontAwesomeIcon icon={faStar} />
                                                <span>{episode.vote_average.toFixed(1)}</span>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                        
                                        {selectedEpisodeId === episode.id && (
                                          <div 
                                            className={`episode-actions ${showEpisodeActions ? 'visible' : ''}`}
                                            style={{ marginTop: '10px' }}
                                          >
                                            <Link 
                                              to={`/watch/${seriesId}${"series" === 'series' ? `?s=${selectedSeason}&e=${episode.episode_number}` : ''}`}
                                              className="action-btn1 action-btn1-pink"
                                              style={isLastWatchedOnDesktop ? { 
                                                minWidth: '120px', 
                                                padding: '5px 8px', 
                                                fontSize: '14px',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                justifyContent: 'center',
                                                textDecoration: 'none' 
                                              } : {}}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleActionClick(episode.id);
                                                handlePlayClick(); // conditionally opens ad link
                                              }}
                                            >
                                              <FontAwesomeIcon icon={faPlay} />
                                              Play
                                            </Link>
                                            <Link 
                                              to={`/alt-watch/${seriesId}${`?s=${selectedSeason}&e=${episode.episode_number}`}`}
                                              className="action-btn1 action-btn1-gray"
                                              style={isLastWatchedOnDesktop ? { 
                                                minWidth: '120px', 
                                                padding: '5px 10px', 
                                                fontSize: '14px',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                justifyContent: 'center',
                                                textDecoration: 'none' 
                                              } : {}}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleActionClick(episode.id);
                                                handlePlayClick();
                                              }}
                                            >
                                              <FontAwesomeIcon icon={faPlay} />
                                              Play2
                                            </Link>
                                            <Link 
                                              to={`/alt-watch-two/${seriesId}${`?s=${selectedSeason}&e=${episode.episode_number}`}`}
                                              className="action-btn1 action-btn1-gray"
                                              style={isLastWatchedOnDesktop ? { 
                                                minWidth: '120px', 
                                                padding: '5px 8px', 
                                                fontSize: '14px',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                justifyContent: 'center',
                                                textDecoration: 'none' 
                                              } : {}}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleActionClick(episode.id);
                                                handlePlayClick();
                                              }}
                                            >
                                              <FontAwesomeIcon icon={faPlay} />
                                              Play3
                                            </Link>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            
                            {hasMoreEpisodes && (
                              <div className="load-more-container">
                                <button 
                                  className="load-more-btn"
                                  onClick={() => setEpisodePage(prev => prev + 1)}
                                >
                                  {t('loadMoreEpisodes', totalEpisodes - currentEpisodes.length)}
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="season-loading">
                            <p>{t('seasonNotReleased')}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {!isMobile && castSection}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {trailer && (
        <TrailerModal 
          isOpen={showTrailerModal}
          onClose={() => setShowTrailerModal(false)}
          youtubeKey={trailer.key}
          modalRef={trailerModalRef}
        />
      )}
      
      {selectedActor && (
        <div className="actor-modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'auto'
        }}>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${actorBackdrop || 'https://via.placeholder.com/1920x1080?text=No+Backdrop'})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(10px)',
            zIndex: -1
          }}></div>
          
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            zIndex: -1
          }}></div>
          
          <div 
            className="actor-modal" 
            ref={actorModalRef}
            style={{
              width: isMobile ? '100%' : '80%',
              maxWidth: isMobile ? 'none' : '1200px',
              backgroundColor: 'transparent',
              borderRadius: isMobile ? '0' : '10px',
              padding: isMobile ? '15px' : '30px',
              position: 'relative',
              maxHeight: isMobile ? '100vh' : '90vh',
              overflowY: 'auto',
              color: 'white',
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}
          >
            <style>{`.actor-modal::-webkit-scrollbar { display: none; }`}</style>
            
            <div className="actor-modal-header" style={{
              display: 'flex',
              justifyContent: 'space-between',
              position: 'absolute',
              top: isMobile ? '15px' : '20px',
              left: isMobile ? '15px' : '20px',
              right: isMobile ? '15px' : '20px',
              zIndex: 10
            }}>
              <div></div>
              <div 
                className="close-actor-modal" 
                onClick={handleCloseActorModal}
                style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                <FontAwesomeIcon icon={faTimes} style={{ color: 'white', fontSize: '18px' }} />
              </div>
            </div>
            
            <div className="actor-content" style={{ marginTop: '80px' }}>
              <div className="actor-header" style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'center',
                marginBottom: '30px',
                textAlign: isMobile ? 'center' : 'left'
              }}>
                <div style={{
                  position: 'relative',
                  width: isMobile ? '120px' : '150px',
                  height: isMobile ? '120px' : '150px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  marginBottom: isMobile ? '15px' : 0,
                  marginRight: isMobile ? 0 : '30px',
                  border: '3px solid rgba(255, 255, 255, 0.2)'
                }}>
                  <img 
                    src={selectedActor.profile_path 
                      ? `https://image.tmdb.org/t/p/w300${selectedActor.profile_path}` 
                      : actorFallbackImage} 
                    alt={selectedActor.name} 
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = actorFallbackImage;
                    }}
                  />
                </div>
                <div>
                  <h2 style={{ 
                    fontSize: isMobile ? '1.5rem' : '2rem', 
                    marginBottom: '10px',
                    marginTop: isMobile ? '10px' : 0 
                  }}>
                    {selectedActor.name}
                  </h2>
                  <p style={{ color: '#aaa', fontSize: '1.1rem' }}>
                    {selectedActor.known_for_department}
                  </p>
                </div>
              </div>
              
              <div className="content-filter2" style={{
                display: 'flex',
                background: 'rgba(30, 30, 30, 0.7)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '30px',
                padding: '0',
                margin: '20px 0 30px',
                width: '100%',
                maxWidth: '300px',
                height: '50px',
                overflow: 'hidden',
                position: 'relative',
                marginLeft: isMobile ? 'auto' : '0',
                marginRight: isMobile ? 'auto' : '0'
              }}>
                <button 
                  className={`filter-btn2 ${contentFilter === 'movies' ? 'active' : ''}`}
                  onClick={() => setContentFilter('movies')}
                  style={{
                    flex: 1,
                    background: contentFilter === 'movies' ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                    border: 'none',
                    color: contentFilter === 'movies' ? 'white' : 'rgba(255, 255, 255, 0.7)',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.3s ease',
                  }}
                >
                  {t('movies')}
                </button>
                <div className="filter-divider2" style={{
                  width: '1px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  height: '100%'
                }}></div>
                <button 
                  className={`filter-btn2 ${contentFilter === 'tv' ? 'active' : ''}`}
                  onClick={() => setContentFilter('tv')}
                  style={{
                    flex: 1,
                    background: contentFilter === 'tv' ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                    border: 'none',
                    color: contentFilter === 'tv' ? 'white' : 'rgba(255, 255, 255, 0.7)',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.3s ease',
                  }}
                >
                  {t('tvShows')}
                </button>
              </div>
              
              <div className="filmography-section">
                <h3 style={{ fontSize: '1.5rem', marginBottom: '20px', textAlign: isMobile ? 'center' : 'left' }}>
                  {t('filmography')}
                </h3>
                
                {actorFilmography ? (
                  <div className="filmography-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? 'repeat(auto-fill, minmax(140px, 1fr))' : 'repeat(auto-fill, minmax(180px, 1fr))',
                    gap: '20px'
                  }}>
                    {filteredFilmography().length > 0 ? (
                      filteredFilmography().map(credit => (
                        <div 
                          key={credit.credit_id}
                          style={{ textAlign: 'center', cursor: 'pointer' }}
                          onClick={() => {
                            if (credit.media_type === 'movie') {
                              handleMovieSelect(credit);
                            } else if (credit.media_type === 'tv') {
                              handleOpenTVShow(credit.id);
                            }
                          }}
                        >
                          <img 
                            src={`https://image.tmdb.org/t/p/w200${credit.poster_path}`}
                            alt={credit.title || credit.name} 
                            style={{
                              width: '100%',
                              height: 'auto',
                              aspectRatio: '2/3',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              marginBottom: '10px'
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/200x300?text=No+Poster';
                            }}
                          />
                          <div style={{ fontWeight: 'bold', marginBottom: '5px', fontSize: '0.9rem' }}>
                            {credit.title || credit.name}
                          </div>
                          <div style={{ color: '#aaa', fontSize: '0.8rem', marginBottom: '5px' }}>
                            {credit.release_date ? new Date(credit.release_date).getFullYear() : 
                             credit.first_air_date ? new Date(credit.first_air_date).getFullYear() : 'N/A'}
                          </div>
                          <StarRating rating={credit.vote_average} size="12px" />
                        </div>
                      ))
                    ) : (
                      <div style={{ 
                        gridColumn: '1 / -1', 
                        textAlign: 'center', 
                        padding: '20px',
                        color: '#aaa'
                      }}>
                        {t('noFilmography', contentFilter === 'movies' ? t('movies').toLowerCase() : t('tvShows').toLowerCase())}
                      </div>
                    )}
                  </div>
                ) : (
                  <p>{t('loadingFilmography')}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {selectedMovie && (
        <div className="movie-modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 3000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'auto'
        }}>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            zIndex: -1
          }}></div>
          
          <MoviesModal 
            movie={selectedMovie}
            onClose={handleCloseMovieModal}
            onBack={handleCloseMovieModal}
            history={navigationStack}
          />
        </div>
      )}
    </>
  );
};

SeriesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  seriesId: PropTypes.number.isRequired,
  onUpdate: PropTypes.func,
  onOpenMovie: PropTypes.func,
  onBack: PropTypes.func,
  history: PropTypes.array
};

export default SeriesModal;